"use client"

import * as React from "react"
import Image from "next/image"
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react"
import SectionTitle from "@/components/SectionTitle"
import { cn } from "@/lib/utils"

export type AdvisorCarouselItem = {
  name: string
  role: string
  image: string
  description: string
  highlights: string[]
  contactHref?: string
  contactLabel?: string
}

type AdvisorCarouselSectionProps = {
  title: string
  subtitle?: string
  items: AdvisorCarouselItem[]
  className?: string
}

export default function AdvisorCarouselSection({
  title,
  subtitle,
  items,
  className,
}: AdvisorCarouselSectionProps) {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [isPaused, setIsPaused] = React.useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setPrefersReducedMotion(mq.matches)

    onChange()

    try {
      mq.addEventListener("change", onChange)
      return () => mq.removeEventListener("change", onChange)
    } catch {
      mq.addListener(onChange)
      return () => {
        try {
          mq.removeListener(onChange)
        } catch {}
      }
    }
  }, [])

  React.useEffect(() => {
    if (items.length <= 1 || isPaused || prefersReducedMotion) return

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length)
    }, 5200)

    return () => window.clearInterval(timer)
  }, [items.length, isPaused, prefersReducedMotion])

  React.useEffect(() => {
    setActiveIndex((current) => (current >= items.length ? 0 : current))
  }, [items.length])

  if (!items.length) return null

  const goTo = (index: number) => {
    setActiveIndex(((index % items.length) + items.length) % items.length)
  }

  const previous = () => goTo(activeIndex - 1)
  const next = () => goTo(activeIndex + 1)

  return (
    <section className={cn("py-12 md:py-16", className)}>
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <div className="mb-8 text-center">
          <p
            className="mb-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#fb0009]"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Equipo LET
          </p>
          <SectionTitle text={title} variant="light" className="text-4xl md:text-5xl" />
          {subtitle && (
            <p
              className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-zinc-600 md:text-base"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {subtitle}
            </p>
          )}
        </div>

        <div className="relative overflow-hidden rounded-[36px] border border-zinc-200 bg-white shadow-[0_26px_90px_rgba(15,23,42,0.10)]">
          <div className="absolute -left-24 top-10 h-56 w-56 rounded-full bg-[#fb0009]/8 blur-3xl" aria-hidden="true" />
          <div className="absolute -right-24 bottom-10 h-64 w-64 rounded-full bg-black/5 blur-3xl" aria-hidden="true" />

          <div
            className="relative"
            role="region"
            aria-roledescription="carousel"
            aria-label={title}
            tabIndex={0}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault()
                previous()
              } else if (event.key === "ArrowRight") {
                event.preventDefault()
                next()
              }
            }}
          >
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out will-change-transform"
                style={{ transform: `translate3d(-${activeIndex * 100}%, 0, 0)` }}
              >
                {items.map((item, index) => (
                  <div
                    key={item.name}
                    className="w-full shrink-0"
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} de ${items.length}`}
                  >
                    <article className="grid min-h-[34rem] overflow-hidden border-t border-zinc-200 md:grid-cols-[minmax(280px,0.92fr)_minmax(0,1.08fr)]">
                        <div className="relative min-h-[20rem] bg-zinc-100 md:min-h-[34rem]">
                          <Image
                            src={item.image}
                            alt={item.name}
                            fill
                            priority={index === 0}
                            sizes="(min-width: 1024px) 38vw, 100vw"
                            className="object-cover object-center"
                          />
                        </div>

                        <div className="flex flex-col gap-6 bg-white p-6 md:p-8 lg:p-10">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="max-w-3xl">
                              <h3 className="mt-3 text-3xl font-bold text-zinc-950 md:text-[2.5rem]" style={{ fontFamily: "DIN, sans-serif" }}>
                                {item.name}
                              </h3>
                              <p
                                className="mt-2 text-base font-semibold uppercase tracking-[0.18em] text-zinc-700"
                                style={{ fontFamily: "Montserrat, sans-serif" }}
                              >
                                {item.role}
                              </p>
                            </div>
                          </div>

                          <div
                            className="max-w-3xl space-y-4 text-base leading-8 text-zinc-600 md:text-lg"
                            style={{ fontFamily: "Montserrat, sans-serif" }}
                          >
                            {item.description
                              .split(/\n\s*\n/)
                              .filter((paragraph) => paragraph.trim().length > 0)
                              .map((paragraph, paragraphIndex) => (
                                <p key={paragraphIndex}>{paragraph}</p>
                              ))}
                          </div>

                          <div>
                            <p
                              className="text-[11px] font-semibold uppercase tracking-[0.24em] text-zinc-500"
                              style={{ fontFamily: "Montserrat, sans-serif" }}
                            >
                              Lo que coordina
                            </p>
                            <div className="mt-4 flex flex-wrap gap-3">
                              {item.highlights.map((highlight) => (
                                <span
                                  key={highlight}
                                  className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm"
                                >
                                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[#fb0009]" />
                                  {highlight}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-zinc-200 bg-white/85 px-5 py-4 md:px-8">
            <div className="text-sm text-zinc-600" style={{ fontFamily: "Montserrat, sans-serif" }}>
              Desliza o usa las flechas para ver al siguiente asesor.
            </div>

            <div className="flex items-center gap-2" aria-label="Indicadores del carrusel">
              {items.map((item, index) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => goTo(index)}
                  className={cn(
                    "h-2.5 rounded-full transition-all",
                    index === activeIndex ? "w-8 bg-[#fb0009]" : "w-2.5 bg-zinc-300 hover:bg-zinc-400"
                  )}
                  aria-label={`Ir a la tarjeta ${index + 1}`}
                  aria-pressed={index === activeIndex}
                >
                  <span className="sr-only">{item.name}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={previous}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-700 shadow-sm transition hover:border-zinc-950 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Card anterior"
                disabled={items.length <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={next}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-700 shadow-sm transition hover:border-zinc-950 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Siguiente card"
                disabled={items.length <= 1}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
